import { Pencil } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { DraftDestination } from './transferDraft'
import type { DutyHandoffType } from '@/features/tools/types'

// Pairs each handoff type's pill class with its label so the two can't drift
// out of sync; Record<DutyHandoffType, ...> also forces every type to have an
// entry at compile time.
const PILL: Record<DutyHandoffType, { className: string; label: string }> = {
  warm: { className: 'bg-hanna-ui-bg text-hanna-solid', label: 'Warm' },
  smart: { className: 'bg-success-ui-bg text-success-solid', label: 'Smart' },
  cold: { className: 'bg-primary-subtle-bg text-primary-solid', label: 'Cold' },
}

type DestinationChipProps = {
  destination: DraftDestination
  variant: 'collapsed' | 'expanded'
  isFirst?: boolean
  isLast?: boolean
  onEdit?: () => void
  disabled?: boolean
}

function DestinationLines({ destination }: { destination: DraftDestination }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 p-1">
      <span className="text-sm font-medium text-primary-high-contrast">
        {destination.number}
        {destination.extension && (
          <span className="opacity-50"> #{destination.extension}</span>
        )}
      </span>
      {destination.description && (
        <span className="text-sm leading-4 text-text-body font-medium">
          {destination.description}
        </span>
      )}
    </div>
  )
}

export function TransferDestinationChip({
  destination,
  variant,
  isFirst,
  isLast,
  onEdit,
  disabled,
}: DestinationChipProps) {
  // View mode
  if (variant === 'collapsed') {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-secondary bg-muted p-3">
        <DestinationLines destination={destination} />
      </div>
    )
  }

  // Edit list mode — handoff type is this destination's own state
  const { className: pillClass, label: pillLabel } =
    PILL[destination.handoffType]
  return (
    <div
      className={cn(
        'group relative flex items-start justify-between border border-border bg-white py-1 pr-1 pl-3 hover:bg-muted',
        isFirst ? 'rounded-t-lg' : '-mt-px',
        isLast && 'rounded-b-lg'
      )}>
      <DestinationLines destination={destination} />
      <span
        className={cn(
          'inline-flex w-12.5 justify-center rounded-full px-2 py-1 text-xs font-medium',
          pillClass,
          !disabled && 'group-hover:opacity-0 group-focus-within:opacity-0'
        )}>
        {pillLabel}
      </span>
      {!disabled && (
        // opacity (not `hidden`) keeps the button in the tab order and a11y
        // tree; absolute so it overlays the pill without shifting layout.
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Edit destination"
          onClick={onEdit}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100">
          <Pencil className="size-4.5" />
        </Button>
      )}
    </div>
  )
}
